"use client";

import type { ComponentType } from "react";
import { Store } from "lucide-react";
import GraphBarIncreaseSquareRemixIcon from "@iconify-react/streamline-flex/graph-bar-increase-square-remix";
import ConnectionIcon from "@/components/home/connection-icon";
import { useRevealOnce } from "@/lib/hooks/use-reveal-once";
import { useTranslation } from "@/lib/hooks/use-translation";

type Feature = {
  /** The three glyphs come from three sources (lucide, iconify's CSS-mask
      component, and a local SVG) whose prop types don't line up, so this is
      narrowed to just what the row actually passes. */
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  /** Key of a two-string array in the `home.features.items` namespace — the
      copy is split exactly where the comp breaks the line, in each language,
      so the balance survives translation. */
  key: string;
};

const FEATURES: Feature[] = [
  { icon: Store, key: "manage" },
  { icon: GraphBarIncreaseSquareRemixIcon, key: "insight" },
  { icon: ConnectionIcon, key: "connect" },
];

/**
 * Features — v2 section 2: the three-up capability row under the hero.
 *
 * Motion: this sits below the fold, so nothing runs until `useRevealOnce`
 * flips `is-visible` on the wrapper. From there the cascade is staggered by
 * `--feat-d` (one value per column) — heading rises, each glass tile springs
 * in with an overshoot, its glyph drops a beat later, a sheen sweeps the
 * glass, and the copy slides in from the tile's side. The tiles then keep a
 * desynced idle bob, and hovering a column wiggles its glyph.
 * All of it is CSS keyframes (see globals.css, "Section 2 · feature row"):
 * no motion bundle on the critical path, and reduced-motion drops straight to
 * the final state.
 */
export default function Features() {
  const { t } = useTranslation();
  const ref = useRevealOnce<HTMLElement>(0.2);

  return (
    <section
      ref={ref}
      id="features"
      aria-label={t("home.features.aria")}
      // `scroll-mt-13` clears the fixed header for the navbar drawer's link here.
      className="feat-reveal scroll-mt-13 bg-[#FCFCFC]"
    >
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-14 px-4 py-12 md:px-[80px]">
        <h2 className="feat-title self-stretch text-center text-[26px]/[38px] font-semibold text-black [text-wrap:balance] sm:text-[32px]/[44px] lg:text-[40px]/[48px]">
          {t("home.features.heading")}
        </h2>

        {/* Stacked on mobile: `w-fit` shrinks the list to its widest row so every
            tile lines up on one left edge instead of each row centring itself. */}
        <ul className="mx-auto flex w-fit flex-col items-start gap-10 lg:w-full lg:flex-row lg:items-center lg:justify-between lg:gap-8">
          {FEATURES.map((feature, i) => {
            const lines = t(`home.features.items.${feature.key}`, {
              returnObjects: true,
            }) as unknown as [string, string];

            return (
              <li
                key={feature.key}
                // One custom property per column drives every layer's delay, so
                // the cascade stays in step (and reaches the ::after sheen,
                // which can't take an inline animation-delay).
                style={{ "--feat-d": `${i * 0.14}s` } as React.CSSProperties}
                className="feat-item flex items-center gap-6 lg:gap-8"
              >
                {/* Hover lift is a transition on this wrapper, the idle bob an
                  animation on the next, and the entrance spring on the tile
                  itself — three layers so the transforms never fight. */}
                <span className="shrink-0 transition-transform duration-300 ease-out [.feat-item:hover_&]:-translate-y-1.5 [.feat-item:hover_&]:scale-[1.06]">
                  <span className="feat-bob block">
                    <span className="feat-tile grid size-[72px] place-items-center">
                      {/* Second translucent plate from the comp, sitting between
                        the glass and the glyph. */}
                      <span
                        aria-hidden
                        className="absolute inset-0 bg-[rgba(249,249,249,0.9)] backdrop-blur-[3.84px]"
                      />
                      {/* Two elements, not one: the entrance lives on the
                        wrapper and the hover wiggle on the glyph inside it.
                        Sharing an element meant `:hover` replacing the
                        `animation` shorthand, so leaving the icon dropped it
                        back onto its entrance — which restarted from frame
                        zero, delay and all, and `both` holds that first frame
                        (opacity 0, scale 0.3). The icon vanished for a beat
                        and dropped in again every time the pointer left. */}
                      <span className="feat-glyph relative block">
                        <feature.icon
                          aria-hidden
                          className="feat-glyph-art size-10 text-[#0077D4]"
                        />
                      </span>
                    </span>
                  </span>
                </span>

                <p className="feat-copy text-[17px]/[26px] text-black/60 sm:text-[20px]/[28px]">
                  {lines[0]}
                  <br />
                  {lines[1]}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
